import React from "react"
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Avatar,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
	Alert,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	FormHelperText,
} from "@mui/material"
import {Controller, useForm, useFieldArray} from "react-hook-form"
import {zodResolver} from "@hookform/resolvers/zod"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"

import {offerFormSchemaV2, OfferFormDataV2} from "./offerFormValidation"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type CreateOfferModalV2Props = {
	customerID: string
	onClose: () => void
	onSuccess: () => void
}

function CreateOfferModalV2({customerID, onClose, onSuccess}: CreateOfferModalV2Props) {
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [loading, setLoading] = React.useState(false)
	const [offerID] = React.useState(crypto.randomUUID())

	const {
		control,
		handleSubmit,
		watch,
		formState: {errors},
	} = useForm<OfferFormDataV2>({
		resolver: zodResolver(offerFormSchemaV2),
		defaultValues: {
			startDate: "",
			endDate: "",
			notes: "",
			oneOff: {
				enabled: true,
				discountRate: 30,
			},
			paymentPlan: {
				enabled: false,
				tiers: [
					{
						id: crypto.randomUUID(),
						discountRate: 20,
						maxTimeToPayWeeks: 4,
						confirmed: true,
					},
					{
						id: crypto.randomUUID(),
						discountRate: 25,
						maxTimeToPayWeeks: 8,
						confirmed: false,
					},
				],
			},
		},
	})

	const {fields: tierFields} = useFieldArray({
		control,
		name: "paymentPlan.tiers",
	})

	const watchOneOffEnabled = watch("oneOff.enabled")
	const watchPaymentPlanEnabled = watch("paymentPlan.enabled")

	const onSubmit = async (data: OfferFormDataV2) => {
		setLoading(true)
		try {
			const tiers: any[] = []

			if (data.oneOff?.enabled && data.oneOff?.discountRate !== undefined) {
				tiers.push({
					tierId: crypto.randomUUID(),
					tierType: "ONE_OFF",
					discountRate: data.oneOff.discountRate / 100,
				})
			}

			if (data.paymentPlan?.enabled && data.paymentPlan?.tiers) {
				data.paymentPlan.tiers.forEach((tier) => {
					tiers.push({
						tierId: tier.id,
						tierType: "PLAN",
						discountRate: tier.discountRate / 100,
						timeFrameWeeks: tier.maxTimeToPayWeeks,
					})
				})
			}

			await api.customer.createOffer(customerID, {
				offerId: offerID,
				startDate: data.startDate,
				endDate: data.endDate,
				tiers,
				agentEmail: agentEmail || undefined,
				notes: data.notes || undefined,
			})

			notification.success("Customer offer created successfully")
			onSuccess()
		} catch (error: any) {
			notification.error(error.message || "Failed to create customer offer")
		} finally {
			setLoading(false)
		}
	}

	const hasErrors = Object.keys(errors).length > 0
	const enabledCount = (watchOneOffEnabled ? 1 : 0) + (watchPaymentPlanEnabled ? 1 : 0)

	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Create new offer</DialogTitle>
			<form onSubmit={handleSubmit(onSubmit)}>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" paragraph>
						Set up a new payment offer with one or more payment options.
					</Typography>

					<Card sx={{backgroundColor: "#FAFAFA", border: "1px solid #E9EAEB", mb: 3}} elevation={0}>
						<CardHeader
							avatar={<CalendarTodayIcon />}
							title="Offer period"
							subheader="This offer will be available to the customer in the given time period."
						/>
						<CardContent>
							<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
								<Controller
									name="startDate"
									control={control}
									render={({field}) => (
										<TextField
											{...field}
											label="Start date *"
											type="date"
											InputLabelProps={{shrink: true}}
											error={!!errors.startDate}
											helperText={errors.startDate?.message}
											fullWidth
											size="small"
										/>
									)}
								/>
								<Controller
									name="endDate"
									control={control}
									render={({field}) => (
										<TextField
											{...field}
											label="End date *"
											type="date"
											InputLabelProps={{shrink: true}}
											error={!!errors.endDate}
											helperText={errors.endDate?.message}
											fullWidth
											size="small"
										/>
									)}
								/>
							</Box>
						</CardContent>
					</Card>

					<Box display="flex" alignItems="center" gap={1} mb={2}>
						<Typography variant="h6">Payment options</Typography>
						<Chip label={`${enabledCount}/2 options`} size="small" />
					</Box>
					<Typography variant="body2" color="textSecondary" paragraph>
						Add one or both option types to create your offer.
					</Typography>

					<Card sx={{backgroundColor: "#CAE8FA", border: "1px solid #66BDEE", mb: 2}} elevation={0}>
						<CardHeader
							avatar={<Avatar style={{backgroundColor: "#bdbdbd"}}>OP</Avatar>}
							title="One-off settlement"
							subheader="Customer settles the balance with a single payment."
							action={
								watchOneOffEnabled ? (
									<Box display="flex" alignItems="center" gap={1}>
										<Controller
											name="oneOff.discountRate"
											control={control}
											render={({field: {value}}) =>
												value ? <Chip label={`${value}% discount`} size="small" /> : <></>
											}
										/>
										<IconButton size="small">
											<EditIcon fontSize="small" />
										</IconButton>
									</Box>
								) : null
							}
						/>
					</Card>

					<Card sx={{backgroundColor: "#D9E0FD", border: "1px solid #91A2F3"}} elevation={0}>
						<CardHeader
							avatar={<Avatar style={{backgroundColor: "#bdbdbd"}}>PP</Avatar>}
							title="Payment plan settlement"
							subheader="Customer settles the balance over multiple payments."
						/>
						<Card
							sx={{backgroundColor: "#ECF0FE", border: "1px solid #91A2F3", mt: 2, ml: 2, mr: 2}}
							elevation={0}
						>
							<CardContent>
								<Typography variant="subtitle2" fontWeight="medium" gutterBottom>
									Payment plan settlement options
								</Typography>

								{tierFields.map((tier, index) => (
									<Box
										key={tier.id}
										sx={{backgroundColor: "#FFFFFF", border: "1px solid #91A2F3", mb: 2, p: 2}}
									>
										<Box display="flex" alignItems="center" gap={1} mb={1}>
											<Box
												sx={{
													width: 20,
													height: 20,
													borderRadius: "50%",
													backgroundColor: "#7c3aed",
													color: "white",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: 10,
													fontWeight: 600,
												}}
											>
												{index + 1}
											</Box>
											<Typography variant="body2" fontWeight="medium">
												PP - Option {index + 1}
											</Typography>
											{tier.confirmed && <Chip label="Confirmed" size="small" />}
											<Box flexGrow={1} />
											<IconButton size="small">
												{tier.confirmed ? (
													<EditIcon fontSize="small" />
												) : (
													<CloseIcon fontSize="small" />
												)}
											</IconButton>
										</Box>
										<Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mb={2}>
											<Controller
												name={`paymentPlan.tiers.${index}.maxTimeToPayWeeks`}
												control={control}
												render={({field: {value, onChange}}) => (
													<FormControl fullWidth size="small">
														<InputLabel>Up to</InputLabel>
														<Select value={value} onChange={onChange}>
															<MenuItem value={4}>4</MenuItem>
															<MenuItem value={6}>6</MenuItem>
															<MenuItem value={8}>8</MenuItem>
															<MenuItem value={10}>10</MenuItem>
														</Select>
													</FormControl>
												)}
											/>
											<Controller
												name={`paymentPlan.tiers.${index}.discountRate`}
												control={control}
												render={({field: {value, onChange}}) => (
													<FormControl
														fullWidth
														size="small"
														error={!!errors.paymentPlan?.tiers?.[index]?.discountRate}
													>
														<InputLabel>Discount %</InputLabel>
														<Select value={value} onChange={onChange}>
															<MenuItem value={15}>15</MenuItem>
															<MenuItem value={20}>20</MenuItem>
															<MenuItem value={25}>25</MenuItem>
															<MenuItem value={30}>30</MenuItem>
														</Select>
														{errors.paymentPlan?.tiers?.[index]?.discountRate && (
															<FormHelperText>
																{errors.paymentPlan.tiers[index]?.discountRate?.message}
															</FormHelperText>
														)}
													</FormControl>
												)}
											/>
										</Box>
										<Typography variant="caption" color="textSecondary">
											Customer receives {watch(`paymentPlan.tiers.${index}.discountRate`)}%
											discount if they complete payment up to{" "}
											{watch(`paymentPlan.tiers.${index}.maxTimeToPayWeeks`)} weeks
										</Typography>
									</Box>
								))}

								{hasErrors && (
									<Alert severity="error" icon={<ErrorOutlineIcon />}>
										<Typography variant="body1" fontWeight="medium">
											Configuration errors detected
										</Typography>
										<Typography variant="body2">
											Please fix the errors highlighted above before creating the settlement
											offer.
										</Typography>
									</Alert>
								)}
							</CardContent>
						</Card>
					</Card>

					<Box mt={3}>
						<Typography variant="h6" gutterBottom>
							Internal notes
						</Typography>
						<Typography variant="body2" color="textSecondary" paragraph>
							For agent reference only (not visible to customer).
						</Typography>
						<Controller
							name="notes"
							control={control}
							render={({field}) => (
								<TextField
									{...field}
									placeholder="Add relevant info about this offer"
									multiline
									minRows={3}
									fullWidth
									size="small"
								/>
							)}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button type="submit" color="primary" variant="contained" disabled={loading || hasErrors}>
						{loading ? <CircularProgress size={24} /> : "Create Offer"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	)
}

export {CreateOfferModalV2}
